import { rgba } from "polished";
import { FC, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { useBandContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { IBandUserWithPermissions } from "Bands/types";
import { useBandRoute, usePermissionsGroups } from "Bands/utils";
import { copy } from "shared/assets";
import { Close } from "shared/assets/icons";
import { IActionDropdownItem, Table, TableColumn } from "shared/components";
import { Spacer } from "shared/layout";
import { Icon, MutedDarkText, SmallText } from "shared/styles";
import { colors, space } from "shared/styles/constants";
import { getStatus } from "shared/styles/defaults";
import { IPermission, IStatus } from "shared/types";

const {
  permissions: { permissionGroup: permissionCopy },
} = copy;

interface PermissionsTableProps {
  users: IBandUserWithPermissions[];
}
export const PermissionsTable: FC<PermissionsTableProps> = ({ users }) => {
  const { rootGroups, childrenGroups } = usePermissionsGroups({
    permissions: users[0].permissions,
  });
  const { pushRoute } = useBandRoute();
  const history = useHistory();
  const { getCategory } = useBandContext();

  const renderPermissionIcon = (granted: boolean) => {
    const { icon: successIcon, iconColor: successColor = "" } = getStatus(IStatus.success);

    const { iconColor: errorColor = "" } = getStatus(IStatus.error);

    return granted ? (
      <Icon
        as={successIcon}
        isCircle
        color={successColor}
        size="xxxl"
        padding={space.xxs}
        backgroundColor={rgba(successColor, 0.2)}
      />
    ) : (
      <Icon
        as={Close}
        isCircle
        color={errorColor as colors}
        size="xxxl"
        padding={space.xxs}
        backgroundColor={rgba(errorColor, 0.2) as colors}
      />
    );
  };

  const getRootColumns = useCallback(
    (index: number) => {
      return childrenGroups?.[index]?.map(
        child =>
          ({
            id: child.id,
            Header: child.label,
            description: child.description,
            Cell: ({ row }) => {
              const user = row.original as IBandUserWithPermissions;
              const topPermission = user.permissions.find(perm => perm.permission?.id === child.id);

              if (!topPermission) return null;

              return renderPermissionIcon(topPermission.granted);
            },
            columns: (() => {
              // permission without group
              if ("permissionGroup" in child) {
                const topPermission = users[0].permissions.find(
                  perm => perm.permission?.id === child.id
                );

                if (topPermission) {
                  if (users[0].permissions.length === 1) return null;
                  return [
                    {
                      id: `${child.id}_child`,
                      Header: "",
                      accessor: "permissions",
                      Cell: ({ value }: { value: IPermission[] }) => {
                        const perm = value.find(({ permission: { id } }) => id === child.id);

                        if (!perm) return null;

                        return renderPermissionIcon(perm.granted);
                      },
                    },
                  ];
                }
              }

              // permission group
              const groupPermissions = users[0].permissions.filter(
                perm => perm.permission.permissionGroup?.id === child.id
              );

              if (!groupPermissions) return [];

              return groupPermissions.map(({ permission: { shortLabel, id: permId } }) => ({
                id: permId,
                Header: shortLabel,
                accessor: "permissions",
                Cell: ({ value }: { value: IPermission[] }) => {
                  const perm = value.find(({ permission: { id } }) => id === permId);

                  if (!perm) return null;

                  return renderPermissionIcon(perm.granted);
                },
              }));
            })(),
          } as TableColumn)
      );
    },
    [childrenGroups, users]
  );

  const userColumn: TableColumn = useMemo(
    () => ({
      Header: "",
      accessor: "firstname",
      Cell: ({ row }) => {
        const invitation = row.original as IBandUserWithPermissions;
        return (
          <Spacer size="xxs">
            <span>
              {invitation.firstname} {invitation.lastname}
            </span>
            <SmallText as={MutedDarkText}>{getCategory(invitation.category?.id)?.label}</SmallText>
          </Spacer>
        );
      },
    }),
    [getCategory]
  );

  const columns: TableColumn[] = useMemo(() => {
    const rootColumns: TableColumn[] = rootGroups
      ? rootGroups.map(({ label, id, description }, index) => ({
          id,
          Header: label,
          description: description,
          columns: getRootColumns(index),
        }))
      : [];

    return [userColumn, ...rootColumns];
  }, [getRootColumns, rootGroups, userColumn]);

  const getRowActions = useCallback(
    (userId: string): IActionDropdownItem[] => {
      const currentUser = users.find(({ id }) => id === userId);

      if (!currentUser) return [];

      const actions: IActionDropdownItem[] = [
        {
          label: permissionCopy.actions.editAllPermissions,
          handleClick: () =>
            pushRoute(BAND_PATHS.EDIT_PERMISSION, {
              params: { userId },
              state: { from: history.location.pathname },
              hash: `root-group-id-${rootGroups ? rootGroups[0].id : ""}`,
            }),
          dataTestId: "edit_permissions_action",
        },
      ];

      return actions;
    },
    [history.location, pushRoute, users, rootGroups]
  );

  return (
    <Table
      columns={columns}
      data={users}
      getRowActions={getRowActions}
      options={{
        isStriped: false,
        isOutline: true,
        isMultiHeading: true,
        width: "max-content",
      }}
    />
  );
};
